// The member side of page layouts (layout.js has the rules):
//
//   const sections = useLayout(product, 'week')        // [{ key, shown, fixed }], null while it loads
//   <LayoutSections sections={sections} parts={{
//     video:   { place: 'full', render: () => <Video /> },
//     summary: { place: 'main', render: () => <Summary />, columns: '872px minmax(0, 1fr)' },
//     hw:      { place: 'side', render: () => <Homework />, when: items.length > 0 },
//   }} />
//
// place: 'full' (the whole width), 'main' (a band's left column) or 'side' (its right column);
// columns: the band's grid on a laptop, from the first section in the band that sets it; when: false
// leaves the section out (nothing to show). On a phone, and below 1100px, every section is 1 column
// in the layout's order.
import { useEffect, useState } from 'react'
import { useMedia } from '../lib/media.js'
import { loadLayout } from './data.js'
import { defaultLayoutFor, layoutBands, resolveLayout, shownKeys } from './layout.js'

export const STACK_QUERY = '(max-width: 1099px)'
const cache = new Map()

// The published layout of a product's page, resolved against the default (its own, or the one for
// the product: layout.js defaultLayoutFor, so POYT's home is POYT_HOME and a mastermind's
// MASTERMIND_HOME)
export function useLayout(product, page, defaults = null) {
  const base = defaults || defaultLayoutFor(product, page)
  const key = product ? `${product.id}:${page}` : null
  const [got, setGot] = useState(() => (key && cache.has(key) ? { key, value: cache.get(key) } : { key: null }))
  useEffect(() => {
    if (!key) return undefined
    let live = true
    if (cache.has(key)) { setGot({ key, value: cache.get(key) }); return undefined }
    loadLayout(product.id, page)
      .then(row => { cache.set(key, row?.sections || null); if (live) setGot({ key, value: row?.sections || null }) })
      // a layout that doesn't load leaves the default, which is what the page was built for
      .catch(() => { if (live) setGot({ key, value: null }) })
    return () => { live = false }
  }, [key])
  if (!base) return []
  if (got.key !== key) return null
  return resolveLayout(got.value, base)
}

// Forget the layouts read so far (a test, or after Adam publishes in the same tab)
export function clearLayoutCache() {
  cache.clear()
}

export function LayoutSections({ sections, parts, className = '' }) {
  const stacked = useMedia(STACK_QUERY)
  if (!sections) return null
  if (stacked) {
    return (
      <div className={`pp-layout pp-layout--stack ${className}`.trim()}>
        {shownKeys(sections, parts).map(key => <Section key={key} id={key} part={parts[key]} />)}
      </div>
    )
  }
  return (
    <div className={`pp-layout ${className}`.trim()}>
      {layoutBands(sections, parts).map(b => {
        if (b.full) return <Section key={b.full} id={b.full} part={parts[b.full]} />
        const both = b.main.length && b.side.length
        // keyed by its first section, so a section showing up later above it (Adam's announcement once
        // the Library has loaded) doesn't redraw the row and lose what's open in it
        return (
          <div key={`band-${b.main[0] || b.side[0]}`} className={both ? 'pp-band' : 'pp-band pp-band--single'}
            style={both && b.columns ? { gridTemplateColumns: b.columns } : undefined}>
            {b.main.length > 0 && <div className="pp-band-col">{b.main.map(k => <Section key={k} id={k} part={parts[k]} />)}</div>}
            {b.side.length > 0 && <div className="pp-band-col">{b.side.map(k => <Section key={k} id={k} part={parts[k]} />)}</div>}
          </div>
        )
      })}
    </div>
  )
}

function Section({ id, part }) {
  return <div className="pp-section" data-section={id}>{part.render()}</div>
}
